import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ToastProvider, ToastInitializer } from '@/components/Toast';
import { Layout } from '@/components/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { LandingPage } from '@/pages/LandingPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { POSPage } from '@/pages/POSPage';
import { ProductsPage } from '@/pages/ProductsPage';
import { CustomersPage } from '@/pages/CustomersPage';
import { SalesPage } from '@/pages/SalesPage';
import { ConfiguracoesPage } from '@/pages/ConfiguracoesPage';
import { PlansPage } from '@/pages/PlansPage';
import { SubscriptionPage } from '@/pages/SubscriptionPage';
import { SuperAdminPage } from '@/pages/SuperAdminPage';
import { ReportsPage } from '@/pages/ReportsPage';
import ReportsModulesPage from '@/pages/ReportsModulesPage';
import CashFlowModulesPage from '@/pages/CashFlowModulesPage';
import { CatalogoPage } from '@/pages/CatalogoPage';
import { PedidosOnlinePage } from '@/pages/PedidosOnlinePage';
import { WhatsAppPage } from '@/pages/WhatsAppPage';
import WhatsAppOrdersPage from '@/pages/WhatsAppOrdersPage';
import { UsersPage } from '@/pages/UsersPage';
import { RelatoriosCaixaPage } from '@/pages/RelatoriosCaixaPage';
import { MarketplacesPage } from '@/pages/MarketplacesPage';
import { RemoteSupportPage } from '@/pages/RemoteSupportPage';
import { FirebirdMigrationPage } from '@/pages/FirebirdMigrationPage';
import EmitirNFePage from '@/pages/EmitirNFePage';
import GerenciarNFePage from '@/pages/GerenciarNFePage';
import EtiquetasPage from '@/pages/EtiquetasPage';
import PaywallPage from '@/pages/PaywallPage';
import ProposalsPage from '@/pages/ProposalsPage';
import PublicProposalPage from '@/pages/PublicProposalPage';
import ReceiptsPage from '@/pages/ReceiptsPage';
import ContasReceberPage from '@/pages/ContasReceberPage';
import ContasPagarPage from '@/pages/ContasPagarPage';
import FornecedoresPage from '@/pages/FornecedoresPage';
import RelatorioVendasPage from '@/pages/RelatorioVendasPage';
import PartnerDashboardPage from '@/pages/PartnerDashboardPage';
import GerenciarParceirosPage from '@/pages/GerenciarParceirosPage';
import PartnerMinisitePage from '@/pages/PartnerMinisitePage';
import PartnerMinisiteEditorPage from '@/pages/PartnerMinisiteEditorPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import SegmentLandingPage from '@/pages/SegmentLandingPage';
import CityLandingPage from '@/pages/CityLandingPage';
import ModuleLockedPage from '@/pages/ModuleLockedPage';
import { useTenantModules } from '@/lib/useTenantModules';
import { InstallPWA } from '@/components/InstallPWA';
import WhatsAppOrderNotifier from '@/components/WhatsAppOrderNotifier';
import '@/App.css';

// Interceptor global: detecta HTTP 402 (assinatura bloqueada) e redireciona para /paywall
const installSubscriptionInterceptor = () => {
  if (typeof window === 'undefined' || window.__subGuardInstalled) return;
  window.__subGuardInstalled = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const resp = await origFetch(...args);
    try {
      if (resp.status === 402 && !window.location.pathname.startsWith('/paywall')
          && !window.location.pathname.startsWith('/login')) {
        // Não redireciona em requests para /paywall/auth/payment (whitelist).
        const url = args[0];
        const urlStr = typeof url === 'string' ? url : (url?.url || '');
        if (!urlStr.includes('/api/payment/') && !urlStr.includes('/api/auth/')) {
          window.location.replace('/paywall');
        }
      }
    } catch (_) { /* noop */ }
    return resp;
  };
};

// Registrar Service Worker para PWA
const registerServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('SW registered:', registration.scope);
          // Detecta atualização do SW e força reload da página assim que o
          // novo worker assume controle. Garante que após o deploy o usuário
          // recebe os bundles novos automaticamente, sem Ctrl+Shift+R.
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (!newWorker) return;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[SW] Nova versão instalada — recarregando para aplicar');
                if (!window.__swReloaded) {
                  window.__swReloaded = true;
                  setTimeout(() => window.location.reload(), 250);
                }
              }
            });
          });
          // Verifica atualizações a cada 60s enquanto a aba estiver aberta
          setInterval(() => registration.update().catch(() => {}), 60000);
        })
        .catch((error) => {
          console.log('SW registration failed:', error);
        });

      // Quando o controlador muda (novo SW assumiu), recarrega 1x
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!window.__swReloaded) {
          window.__swReloaded = true;
          window.location.reload();
        }
      });
    });
  }
};

const ProtectedRoute = ({ children, fullScreen = false, module = null }) => {
  const { user, loading } = useAuth();
  const { modules, loaded } = useTenantModules();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (module && user.role !== 'super_admin') {
    if (!loaded) {
      return <Layout><div className="p-8 text-slate-500">Carregando...</div></Layout>;
    }
    if (!modules?.[module]) {
      return <Layout><ModuleLockedPage moduleKey={module} /></Layout>;
    }
  }

  // PDV em tela cheia, sem sidebar
  if (fullScreen) {
    return children;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  if (user) {
    if (user.role === 'partner') {
      return <Navigate to="/parceiro" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppContent() {
  // Registrar Service Worker
  useEffect(() => {
    registerServiceWorker();
    installSubscriptionInterceptor();
  }, []);

  return (
    <>
      <ToastInitializer />
      <WhatsAppOrderNotifier />
      {/* Banner de instalação PWA */}
      <InstallPWA variant="banner" />
      <Routes>
        <Route path="/paywall" element={<PaywallPage />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos"
          element={
            <ProtectedRoute fullScreen={true} module="pdv">
              <POSPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pdv"
          element={<Navigate to="/pos" replace />}
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <ProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/customers"
          element={
            <ProtectedRoute>
              <CustomersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRoute>
              <SalesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contas-a-receber"
          element={
            <ProtectedRoute>
              <ContasReceberPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contas-a-pagar"
          element={
            <ProtectedRoute>
              <ContasPagarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fornecedores"
          element={
            <ProtectedRoute>
              <FornecedoresPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relatorio-vendas"
          element={
            <ProtectedRoute>
              <RelatorioVendasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parceiro"
          element={
            <ProtectedRoute>
              <PartnerDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parceiro/minisite"
          element={
            <ProtectedRoute>
              <PartnerMinisiteEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/emitir-nfe"
          element={
            <ProtectedRoute>
              <EmitirNFePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gerenciar-nfe"
          element={
            <ProtectedRoute>
              <GerenciarNFePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/etiquetas"
          element={
            <ProtectedRoute>
              <EtiquetasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <Navigate to="/configuracoes" replace />
          }
        />
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/modules"
          element={
            <ProtectedRoute>
              <ReportsModulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashflow/modules"
          element={
            <ProtectedRoute>
              <CashFlowModulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route path="/plans" element={<PlansPage />} />
        <Route
          path="/propostas"
          element={
            <ProtectedRoute>
              <ProposalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recibos"
          element={
            <ProtectedRoute>
              <ReceiptsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <SuperAdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/parceiros"
          element={
            <ProtectedRoute>
              <GerenciarParceirosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suporte-remoto"
          element={
            <ProtectedRoute>
              <RemoteSupportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/configuracoes"
          element={
            <ProtectedRoute>
              <ConfiguracoesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/migracao-firebird"
          element={
            <ProtectedRoute>
              <FirebirdMigrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pedidos-online"
          element={
            <ProtectedRoute module="online">
              <PedidosOnlinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp"
          element={
            <ProtectedRoute module="whatsapp">
              <WhatsAppPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/whatsapp/pedidos"
          element={
            <ProtectedRoute module="whatsapp">
              <WhatsAppOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <ProtectedRoute>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/relatorios-caixa"
          element={
            <ProtectedRoute>
              <RelatoriosCaixaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplaces"
          element={
            <ProtectedRoute module="marketplace">
              <MarketplacesPage />
            </ProtectedRoute>
          }
        />
        <Route path="/catalogo/:slug" element={<CatalogoPage />} />
        <Route path="/proposta/:token" element={<PublicProposalPage />} />
        <Route path="/p/:refCode" element={<PartnerMinisitePage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/pdv-para-acougue" element={<SegmentLandingPage segment="acougue" />} />
        <Route path="/pdv-para-mercado" element={<SegmentLandingPage segment="mercado" />} />
        <Route path="/pdv-para-restaurante" element={<SegmentLandingPage segment="restaurante" />} />
        <Route path="/pdv-para-padaria" element={<SegmentLandingPage segment="padaria" />} />
        <Route path="/pdv-para-lanchonete" element={<SegmentLandingPage segment="lanchonete" />} />
        <Route path="/pdv-para-loja-de-roupas" element={<SegmentLandingPage segment="loja-de-roupas" />} />
        <Route path="/sistema-pdv-sao-paulo" element={<CityLandingPage city="sao-paulo" />} />
        <Route path="/sistema-pdv-belo-horizonte" element={<CityLandingPage city="belo-horizonte" />} />
        <Route path="/sistema-pdv-curitiba" element={<CityLandingPage city="curitiba" />} />
        <Route path="/upgrade/:module" element={<ProtectedRoute><ModuleLockedPage /></ProtectedRoute>} />
        <Route path="/" element={<LandingPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
